import React from 'react';
import {
  Platform,
  TextInput as RNTextInput,
  StyleSheet,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import { mask, unMask } from 'react-native-mask-text';
import { TextInput as RNPTextInput, TextInputProps as RNPTextInputProps } from 'react-native-paper';

import { Caption } from '@components/Typography';
import { usePaperTheme } from '@context/ThemeContext';
import { RuntimeConfig } from '@helpers/RuntimeConfig';

export type TextInputRef = React.ComponentRef<typeof RNTextInput>;
export type TextInputProps = RNPTextInputProps;

export const TextInput = React.forwardRef<TextInputRef, TextInputProps>(function TextInput(
  { style, ...props },
  ref
) {
  return <RNPTextInput ref={ref} style={[styles.input, style]} mode="outlined" dense {...props} />;
});

export const TextInputIcon = RNPTextInput.Icon;
export const TextInputAffix = RNPTextInput.Affix;

export type TextInputWithErrorProps = TextInputProps & {
  errorText: string;
  containerStyle?: ViewStyle;
};

export const TextInputWithError = React.forwardRef<TextInputRef, TextInputWithErrorProps>(
  function TextInputWithError({ errorText, containerStyle, ...props }, ref) {
    const paperTheme = usePaperTheme();

    return (
      <View style={[styles.container, containerStyle]}>
        <TextInput ref={ref} {...props} />
        {errorText ? (
          <Caption
            style={[
              styles.caption,
              {
                color: paperTheme.colors.error,
              },
            ]}>
            {errorText}
          </Caption>
        ) : null}
      </View>
    );
  }
);

export const EmailInputWithError = React.forwardRef<TextInputRef, TextInputWithErrorProps>(
  function EmailInputWithError(props, ref) {
    return (
      <TextInputWithError
        ref={ref}
        autoCapitalize="none"
        autoCorrect={false}
        autoComplete="email"
        textContentType="emailAddress"
        keyboardType="email-address"
        {...props}
      />
    );
  }
);

export const PasswordInputWithError = React.forwardRef<TextInputRef, TextInputWithErrorProps>(
  function PasswordInputWithError(props, ref) {
    const passwordAutofillProps = getPasswordAutofillProps();

    return (
      <TextInputWithError
        ref={ref}
        autoCapitalize="none"
        autoCorrect={false}
        {...props}
        {...passwordAutofillProps}
      />
    );
  }
);

export function getPasswordAutofillProps(
  disablePasswordAutofill = RuntimeConfig.disablePasswordAutofill,
  platformOS: typeof Platform.OS = Platform.OS
): Partial<TextInputProps> {
  if (!disablePasswordAutofill) {
    return {
      autoComplete: 'new-password',
      secureTextEntry: true,
      textContentType: 'newPassword',
    };
  }

  const disabledPasswordAutofillProps: Partial<TextInputProps> = {
    autoComplete: 'off',
    secureTextEntry: false,
  };

  switch (platformOS) {
    case 'ios':
      return {
        ...disabledPasswordAutofillProps,
        passwordRules: null,
        textContentType: 'none',
      };
    case 'android':
      return {
        ...disabledPasswordAutofillProps,
        importantForAutofill: 'no',
      };
    default:
      return disabledPasswordAutofillProps;
  }
}

export type MaskedTextInputProps = Omit<TextInputWithErrorProps, 'value' | 'onChangeText'> & {
  pattern: string;
  onChangeText: (text: string, rawText: string) => void;
  type?: 'custom' | 'currency';
  options?: undefined;
  defaultValue?: string;
};

export function MaskedTextInput({
  pattern,
  type,
  options,
  defaultValue,
  onChangeText,
  ...props
}: MaskedTextInputProps) {
  const defaultValueCustom = defaultValue || '';
  const defaultValueCurrency = defaultValue || '0';

  const initialMaskedValue =
    type === 'currency'
      ? mask(defaultValueCurrency, pattern, type, options)
      : mask(defaultValueCustom, pattern, type, options);

  const [maskedValue, setMaskedValue] = React.useState(initialMaskedValue);

  function onChange(value: string) {
    const newUnMaskedValue = unMask(value, type);
    const newMaskedValue = mask(newUnMaskedValue, pattern, type, options);

    setMaskedValue(newMaskedValue);
    onChangeText(newMaskedValue, newUnMaskedValue);
  }

  return (
    <TextInputWithError value={maskedValue} onChangeText={(value) => onChange(value)} {...props} />
  );
}

type DateInputProps = Omit<MaskedTextInputProps, 'type' | 'pattern' | 'placeholder'>;

export function DateInputWithError(props: DateInputProps) {
  return (
    <MaskedTextInput
      type="custom"
      pattern="99/99/9999"
      placeholder="MM/DD/YYYY"
      keyboardType="number-pad"
      {...props}
    />
  );
}

export const TextInputStyle: TextStyle = {
  // required to prevent overfill
  // see here: https://github.com/callstack/react-native-paper/issues/2581
  textAlign: 'auto',
  width: '100%',
};

const styles = StyleSheet.create({
  caption: {
    paddingHorizontal: 10,
  },
  container: {
    marginVertical: 5,
    width: '100%',
  },
  input: TextInputStyle,
});
